// Data key + its wraps (Implementation Plan v02 §6.2; docs/security/key-custody.md;
// I-1a Batch 3).
//
// - The data key is 32 random bytes, used as a NON-extractable AES-GCM 256 key.
//   Its raw bytes exist only for the moment it is created or unwrapped, then
//   are wiped (best effort — JavaScript cannot promise memory is cleared).
// - It is stored only WRAPPED: AES-GCM under a key derived with Argon2id at the
//   fixed floor from the app-lock secret, and optionally a second wrap under the
//   recovery code. Salt + cost travel with the wrap and are bound as AAD.
// - A photo gets its own random key; the raw key is kept inside its moment's
//   encrypted record (so deleting the moment makes the photo unreadable at once).
//
// Claude never sees a real secret, recovery code or key: tests use synthetic ones.

import type { computeHash } from 'argon2id'

import { aesGcmDecrypt, aesGcmEncrypt } from './aead'
import { type Argon2idCost, ARGON2ID_FLOOR, deriveWrappingKey, SALT_BYTES } from './argon2'
import { type Bytes, randomBytes, toHex, utf8, wipe } from './bytes'

export type WrapPurpose = 'app-lock' | 'recovery'

/** One stored wrap of the data key (kept in the `keyring` store). */
export interface KeyWrap {
  readonly v: 1
  readonly purpose: WrapPurpose
  readonly kdf: 'argon2id'
  readonly cost: Argon2idCost
  readonly salt: Bytes
  readonly iv: Bytes
  readonly wrapped: Bytes
}

export class WrongSecretError extends Error {
  constructor() {
    super('the secret did not unlock the key')
    this.name = 'WrongSecretError'
  }
}

const KEY_BYTES = 32
const NONCE_BYTES = 12
const DATA_KEY_USAGES: KeyUsage[] = ['encrypt', 'decrypt']

function wrapAad(purpose: WrapPurpose, cost: Argon2idCost, salt: Uint8Array): Bytes {
  return utf8(
    JSON.stringify(['feel-keywrap-1', purpose, 'argon2id', cost.memorySize, cost.passes, cost.parallelism, toHex(salt)]),
  )
}

/** Text secrets (passphrase / PIN / recovery code) → bytes, Unicode-normalised. */
export function secretBytes(secret: string): Bytes {
  return utf8(secret.normalize('NFC'))
}

function importAesKey(raw: Bytes): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, DATA_KEY_USAGES)
}

export class KeyWrapFormatError extends Error {
  constructor() {
    super('unknown or damaged key wrap format')
    this.name = 'KeyWrapFormatError'
  }
}

function isBytes(value: unknown, length?: number): value is Bytes {
  return value instanceof Uint8Array && (length === undefined || value.length === length)
}

/** Shape check for a wrap read back from storage, before any work is done with it. */
export function isKeyWrap(value: unknown): value is KeyWrap {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const w = value as Partial<Record<keyof KeyWrap, unknown>>
  const cost = w.cost as Partial<Record<keyof Argon2idCost, unknown>> | undefined
  return (
    w.v === 1 &&
    (w.purpose === 'app-lock' || w.purpose === 'recovery') &&
    w.kdf === 'argon2id' &&
    typeof cost === 'object' &&
    cost !== null &&
    typeof cost.memorySize === 'number' &&
    typeof cost.passes === 'number' &&
    typeof cost.parallelism === 'number' &&
    isBytes(w.salt, SALT_BYTES) &&
    isBytes(w.iv, NONCE_BYTES) &&
    isBytes(w.wrapped, KEY_BYTES + 16)
  )
}

async function wrapRaw(hash: computeHash, raw: Bytes, secret: Uint8Array, purpose: WrapPurpose): Promise<KeyWrap> {
  const cost = { ...ARGON2ID_FLOOR }
  const salt = randomBytes(SALT_BYTES)
  const iv = randomBytes(NONCE_BYTES)
  const wrappingKey = await deriveWrappingKey(hash, secret, salt, cost)
  const wrapped = await aesGcmEncrypt(wrappingKey, iv, wrapAad(purpose, cost, salt), raw)
  return { v: 1, purpose, kdf: 'argon2id', cost, salt, iv, wrapped }
}

/** Derive from the secret and open a wrap; the raw key bytes are the caller's to wipe. */
async function openWrap(hash: computeHash, wrap: KeyWrap, secret: Uint8Array): Promise<Bytes> {
  if (!isKeyWrap(wrap)) {
    throw new KeyWrapFormatError()
  }
  // Refuses a cost outside floor..ceiling (BelowFloorError) before any work.
  const wrappingKey = await deriveWrappingKey(hash, secret, wrap.salt, wrap.cost)
  let raw: Bytes
  try {
    raw = await aesGcmDecrypt(wrappingKey, wrap.iv, wrapAad(wrap.purpose, wrap.cost, wrap.salt), wrap.wrapped)
  } catch {
    throw new WrongSecretError()
  }
  if (raw.length !== KEY_BYTES) {
    wipe(raw)
    throw new KeyWrapFormatError()
  }
  return raw
}

export interface NewKeys {
  /** Non-extractable working key, held in memory only while unlocked. */
  readonly dataKey: CryptoKey
  readonly wraps: readonly KeyWrap[]
}

/**
 * First run: make the data key and wrap it under the app-lock secret, and
 * optionally under a recovery code (pass `recoveryCodeBytes(code)`). Always at
 * ARGON2ID_FLOOR — there is no cost or floor option.
 */
export async function setUpDataKey(
  hash: computeHash,
  appLockSecret: Uint8Array,
  recoverySecret?: Uint8Array,
): Promise<NewKeys> {
  const raw = randomBytes(KEY_BYTES)
  try {
    const wraps = [await wrapRaw(hash, raw, appLockSecret, 'app-lock')]
    if (recoverySecret) {
      wraps.push(await wrapRaw(hash, raw, recoverySecret, 'recovery'))
    }
    return { dataKey: await importAesKey(raw), wraps }
  } finally {
    wipe(raw)
  }
}

/** Unlock: derive from the secret, unwrap, return a non-extractable data key. */
export async function unwrapDataKey(hash: computeHash, wrap: KeyWrap, secret: Uint8Array): Promise<CryptoKey> {
  const raw = await openWrap(hash, wrap, secret)
  try {
    return await importAesKey(raw)
  } finally {
    wipe(raw)
  }
}

/**
 * Re-wrap after the person changes the app-lock secret or adds a recovery code:
 * open the current wrap, wrap the same data key again under the new secret. Same
 * data key, so no record is re-encrypted.
 */
export async function rewrapDataKey(
  hash: computeHash,
  current: KeyWrap,
  currentSecret: Uint8Array,
  newSecret: Uint8Array,
  purpose: WrapPurpose,
): Promise<KeyWrap> {
  const raw = await openWrap(hash, current, currentSecret)
  try {
    return await wrapRaw(hash, raw, newSecret, purpose)
  } finally {
    wipe(raw)
  }
}

// --- Per-photo keys ----------------------------------------------------------

/** New random photo key bytes; stored (base64) inside the moment's encrypted record. */
export function newPhotoKeyBytes(): Bytes {
  return randomBytes(KEY_BYTES)
}

export async function importPhotoKey(raw: Bytes): Promise<CryptoKey> {
  if (raw.length !== KEY_BYTES) {
    throw new Error('photo key has the wrong length')
  }
  return importAesKey(raw)
}
